import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GroceryItem, InputUnit, Meal, PantryRow } from '../../../types';
import { INPUT_UNITS } from '../../../types';
import { asTyped, knownInputUnit, parseAmount } from './pantryUnits';
import QuantityEditor from './QuantityEditor';
import {
  CheckCircle2, Circle, Plus, Trash2, X,
  ChevronLeft, ChevronRight, ShoppingBasket, ShoppingCart, Icon,
} from '../../ui/Icon';

const CHIP = 'ml-1.5 inline-block align-middle px-1.5 py-px rounded-md text-[10px] font-mono tabular-nums bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400';

interface Props {
  items: GroceryItem[];
  meals: Meal[];
  readOnly: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** For the unit a product is already counted in. */
  products: PantryRow[];
  /** `amount` null = one unit. Resolves false to keep what was typed. */
  onAdd: (text: string, amount: number | null, unit: InputUnit) => Promise<boolean>;
  onToggle: (item: GroceryItem, bought: boolean) => void;
  /** Corrects the amount; a line from before ADR-0016 starts counting with it. */
  onEditAmount: (item: GroceryItem, amount: number, unit: InputUnit) => Promise<boolean>;
  onDelete: (itemId: string) => void;
  onClearBought: () => Promise<void>;
}

/**
 * The shopping list, docked beside the plan.
 *
 * It belongs to the trip, not to a day (ADR-0008) — you shop once for several
 * days — so there is nothing to group by here. `mealId` is only a label saying
 * which planned meal an item came from.
 */
export default function GroceryPanel({
  items, meals, readOnly, collapsed, onToggleCollapsed,
  products, onAdd, onToggle, onEditAmount, onDelete, onClearBought,
}: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<InputUnit>('u');
  const [clearing, setClearing] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const mealTitleById = useMemo(
    () => new Map(meals.map(m => [m.id, m.title])),
    [meals],
  );
  const boughtCount = items.filter(i => i.bought).length;

  function changeText(value: string) {
    setText(value);
    const known = knownInputUnit(products, value);
    if (known) setUnit(known);
  }

  async function submit() {
    const value = text.trim();
    if (!value) return;
    const parsed = parseAmount(amount);
    if (parsed === null) return;
    if (await onAdd(value, amount.trim() ? parsed : null, unit)) {
      setText('');
      setAmount('');
      setUnit('u');
    }
  }

  async function clear() {
    setClearing(true);
    try { await onClearBought(); } finally { setClearing(false); }
  }

  if (collapsed) {
    return (
      <button
        onClick={onToggleCollapsed}
        className="shrink-0 w-11 card flex flex-col items-center gap-2 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
        aria-label={t('meals.grocery.expand')}
      >
        <Icon as={ChevronLeft} size={14} className="text-slate-400" />
        <Icon as={ShoppingCart} size={16} className="text-teal-600 dark:text-teal-400" />
        {items.length > 0 && (
          <span className="text-[10px] font-mono text-slate-400">{items.length}</span>
        )}
      </button>
    );
  }

  const pct = items.length > 0 ? Math.round((boughtCount / items.length) * 100) : 0;

  return (
    <aside className="w-full card p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="shrink-0 w-8 h-8 rounded-xl bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center">
          <Icon as={ShoppingCart} size={15} className="text-teal-600 dark:text-teal-400" />
        </div>
        <h2 className="flex-1 min-w-0 text-base font-semibold text-slate-800 dark:text-slate-100 truncate">
          {t('meals.grocery.title')}
        </h2>
        {items.length > 0 && (
          <span className="shrink-0 font-mono text-[11px] tabular-nums text-slate-400">
            {boughtCount}/{items.length}
          </span>
        )}
        <button
          onClick={onToggleCollapsed}
          className="hidden lg:block shrink-0 text-slate-300 hover:text-slate-500 cursor-pointer"
          aria-label={t('meals.grocery.collapse')}
        >
          <Icon as={ChevronRight} size={14} />
        </button>
      </div>

      {items.length > 0 && (
        <div className="h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div
            className="h-full rounded-full bg-teal-500 transition-[width] duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {items.length === 0 ? (
        <div className="flex flex-col items-center text-center gap-1.5 py-5 px-3 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
          <div className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-1">
            <Icon as={ShoppingBasket} size={18} className="text-slate-300 dark:text-slate-600" />
          </div>
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('meals.grocery.empty')}</p>
          {!readOnly && (
            <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">{t('meals.grocery.emptyHint')}</p>
          )}
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-[50vh] overflow-y-auto -mx-1 px-1">
          {items.map(item => {
            const mealTitle = item.mealId ? mealTitleById.get(item.mealId) : undefined;
            return (
              <li key={item.id} className="group flex items-start gap-2 rounded-lg px-1 -mx-1 py-0.5 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors">
                <button
                  onClick={() => onToggle(item, !item.bought)}
                  disabled={readOnly}
                  className="shrink-0 mt-0.5 transition-transform hover:scale-110 disabled:hover:scale-100 disabled:cursor-default cursor-pointer"
                  aria-label={item.text}
                >
                  {item.bought
                    ? <Icon as={CheckCircle2} size={15} className="text-green-500" />
                    : <Icon as={Circle} size={15} className="text-slate-300" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${item.bought ? 'line-through text-slate-400 dark:text-slate-600' : 'text-slate-700 dark:text-slate-200'}`}>
                    {item.text}
                    {editing === item.id ? (
                      <span className="ml-1.5">
                        <QuantityEditor
                          {...(item.amount != null && item.unit
                            ? asTyped(item.amount, item.unit)
                            : { amount: '', unit: 'u' as const })}
                          onSave={async (value, u) => {
                            const ok = await onEditAmount(item, value, u);
                            if (ok) setEditing(null);
                            return ok;
                          }}
                          onCancel={() => setEditing(null)}
                        />
                      </span>
                    ) : readOnly ? (
                      item.quantity && <span className={CHIP}>{item.quantity}</span>
                    ) : (
                      <button
                        onClick={() => setEditing(item.id)}
                        className={`${CHIP} hover:bg-teal-50 hover:text-teal-700 dark:hover:bg-teal-900/30 dark:hover:text-teal-300 cursor-pointer no-underline`}
                        aria-label={t('meals.ingredients.editAmount', { name: item.text })}
                      >
                        {item.quantity ?? t('meals.grocery.setAmount')}
                      </button>
                    )}
                  </p>
                  {mealTitle && (
                    <p className="text-[10px] text-slate-400 dark:text-slate-600 truncate">
                      {t('meals.grocery.forMeal', { title: mealTitle })}
                    </p>
                  )}
                </div>
                {!readOnly && (
                  <button
                    onClick={() => onDelete(item.id)}
                    className="shrink-0 w-5 h-5 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-400 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors cursor-pointer"
                    aria-label={t('common.delete')}
                  >
                    <Icon as={X} size={12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly && (
        <>
          {/* One field, four parts. ADR-0016: everything bought enters the
              pantry, so the amount has a unit; left blank it is one unit. */}
          <div className="flex items-center rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-teal-500/40 focus-within:border-teal-500 transition-all">
            <input
              value={text}
              onChange={e => changeText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
              placeholder={t('meals.grocery.placeholder')}
              className="flex-1 min-w-0 bg-transparent pl-3 pr-2 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
            />
            <input
              value={amount}
              onChange={e => setAmount(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
              inputMode="decimal"
              placeholder="1"
              aria-label={t('meals.grocery.amountLabel')}
              className="w-12 shrink-0 bg-transparent border-l border-slate-100 dark:border-slate-700 px-2 py-2 text-sm font-mono tabular-nums text-slate-700 dark:text-slate-200 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none"
            />
            <select
              value={unit}
              onChange={e => setUnit(e.target.value as InputUnit)}
              aria-label={t('meals.grocery.unitLabel')}
              className="shrink-0 bg-transparent border-l border-slate-100 dark:border-slate-700 pl-1.5 pr-0.5 py-2 text-xs text-slate-600 dark:text-slate-300 focus:outline-none cursor-pointer"
            >
              {INPUT_UNITS.map(u => <option key={u} value={u}>{t(`meals.units.${u}`)}</option>)}
            </select>
            <button
              onClick={submit}
              disabled={!text.trim()}
              className="shrink-0 m-1 w-8 h-8 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center hover:opacity-90 active:scale-[0.97] disabled:opacity-30 disabled:cursor-default transition-all cursor-pointer"
              aria-label={t('meals.grocery.add')}
            >
              <Icon as={Plus} size={15} />
            </button>
          </div>

          {boughtCount > 0 && (
            <button
              onClick={clear}
              disabled={clearing}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-500 transition-colors cursor-pointer disabled:cursor-wait"
            >
              <Icon as={Trash2} size={12} />
              {t('meals.grocery.clearBought')} · {t('meals.grocery.bought', { count: boughtCount })}
            </button>
          )}
        </>
      )}
    </aside>
  );
}
